import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { Brand, Brands } from '../../rest/types/Brand'
import { Configuration } from '../../rest/types/Configuration'
import { Offers } from '../../rest/types/Offer'
import { Category } from '../../rest/types/Category'
import useFooter from '../Footer/useFooter'
import { actions, selectors } from '../../redux'
import { ProductsTemplateProps } from '../../templates/ProductsTemplate'
import { ImageTransformer } from '../Transformers/ImageTransformer'
import Router, { routes } from '../../routes/Router'
import { OfferCardTransformer } from '../Transformers/OfferCardTransformer'
import { ActionButtonVariant } from '../../components/ActionButton/styled'
import { Retailer } from '../../rest/types/Retailer'
import { ProductsFiltersFormValues } from '../../components/ProductsFilters'
import useSortingFilters from '../Filters/useSortingFilters'
import { transformFilters } from '../../helpers/OfferHelpers'
import useHeaderBis from '../header/useHeaderBis'
import { PageType } from '../../rest/types/Page'
import tracking from '../../tracking'

import useFilteredOffers from './useFilteredOffers'

const useOfferList = ({
  offers,
  total,
  brands,
  brand,
  category,
  retailer,
  configuration,
  search,
  filters,
  filtersParams,
}: {
  offers: Offers
  total: number
  brand?: Brand
  category?: Category
  retailer?: Retailer
  configuration: Configuration
  brands?: Brands
  search?: string
  filters?: ProductsFiltersFormValues
  filtersParams?: any
}): ProductsTemplateProps => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isLogin = useSelector(selectors.auth.isConnected)
  const isUserLoading = useSelector(selectors.auth.meIsLoading)
  const hasAccessToStore = useSelector(selectors.auth.hasAccessToStore)
  const headerProps = useHeaderBis(configuration, PageType.Store)
  const footerProps = useFooter(configuration)
  const router = useRouter()
  const isMember = useSelector(selectors.auth.isSubscriptionActive)
  const user = useSelector(selectors.auth.user)
  const [filteredCategories, setFilteredCategories] = useState<Array<string>>()
  const hasTrackedRef = useRef<boolean>(false)

  const defaultSorts = useMemo(
    () => category?.offers?.map((o) => o.id) ?? [],
    [category]
  )

  const { filteredOffers } = useFilteredOffers({
    offers,
    defaultSorts,
  })

  const { props: filtersProps } = useSortingFilters({
    categories:
      brand || retailer || search ? configuration.categories : undefined,
    brands,
    search,
    total,
    hasTrackedRef,
  })

  useEffect(() => {
    if (filters) {
      dispatch(
        actions.filters.setFilters({
          filters: transformFilters(filters as any),
        })
      )
    }
  }, [filters, dispatch])

  useEffect(() => {
    setFilteredCategories(
      configuration.categories
        .filter((category) =>
          filtersParams?.filters.categories.includes(String(category.id))
        )
        .map((category) => category.name)
    )
  }, [configuration.categories, filtersParams?.filters.categories])

  useEffect(() => {
    if (!isUserLoading && !hasTrackedRef.current) {
      tracking.search_centrale(
        search,
        total,
        filteredCategories,
        user?.id,
        user?.sessionId
      )
      hasTrackedRef.current = true
    }
  }, [
    user?.id,
    filteredCategories,
    search,
    total,
    user?.sessionId,
    isUserLoading,
  ])

  return {
    ...(!search && {
      banner: {
        breadcrumb: {
          items: [
            {
              href: t('home_breadcrumb_centrale_page_link'),
              text: t('home_breadcrumb_label'),
            },
            ...(category?.parentCategory?.name
              ? [
                  {
                    text: category?.parentCategory?.name,
                    href: category?.parentCategory?.slug
                      ? Router.getRouteUrl(routes.category, {
                          slug: category?.parentCategory?.slug,
                        })
                      : '',
                  },
                ]
              : []),
            {
              text: category?.name || brand?.name || retailer?.name,
            },
          ],
        },
        ...(retailer && {
          title: retailer.name,
          text: retailer.description,
          imageRight: ImageTransformer(retailer.logo as any),
          ...(retailer.link?.href &&
            retailer.link?.text && {
              linkButton: {
                href: retailer.link?.href,
                target: retailer.link?.target,
                text: retailer.link?.text,
              },
            }),
        }),
        ...(brand && {
          title: brand?.name,
          text: brand?.description,
          imageRight: ImageTransformer(brand?.logo as any),
          ...(brand?.link?.href &&
            brand?.link?.text && {
              linkButton: {
                href: brand?.link?.href,
                target: brand?.link?.target,
                text: brand?.link?.text,
                onClick: () =>
                  tracking.partner_click(
                    brand?.name ?? '',
                    brand?.link?.href ?? '',
                    user?.id ?? ''
                  ),
              },
            }),
        }),
        ...(category && {
          title: category?.name,
          textCategory: category?.shortDescription,
          background: ImageTransformer(category.image as any),
          offersNumber: `${filteredOffers.length} ${
            filteredOffers.length > 1 ? t('offers_label') : t('offer_label')
          }`,
        }),
      },
    }),
    ...((isMember || hasAccessToStore) && {
      sortingFilters: filtersProps,
    }),
    products: {
      items: filteredOffers?.map((offer) =>
        OfferCardTransformer({ offer, t, isLogin: isLogin && hasAccessToStore })
      ),
      noItemText: t('noItemFound'),
    },
    ...(total > filteredOffers.length && {
      seeMoreButton: {
        text: t('products_grid_see_more_result'),
        onClick: () => {
          const url = router.asPath.split('?').shift() ?? ''
          dispatch(actions.filters.increaseLimit())
          dispatch(actions.filters.redirect({ url }))
        },
      },
    }),
    ...(isMember && {
      infoBanner: {
        text: t('unlisted_product_text'),
        button: {
          text: t('unlisted_product_button_text'),
          href: t('unlisted_product_button_link'),
          variant: ActionButtonVariant.secondary,
        },
        isSearchPage: true,
      },
    }),
    headerProps,
    footerProps,
  }
}

export default useOfferList
